import { ButtonClickedEvent, useAnalyticsEvents } from '@atlassian/mpac-analytics';
import { MarketplacePartnerType } from '@atlassian/mpac-types/graphql-gateway-types';
import ExternalLink from '../../primitives/ExternalLink';
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
export const AnalyticsSubjectIds = {
  managelogAccess: 'manageLogAccess',
  termsOfUse: 'termsOfUse',
  privacyPolicy: 'privacyPolicy',
  marketplaceTermsOfUse: 'marketplaceTermsOfUse'
};
const MissingPolicyWarning = _ref => {
  let {
    partnerName,
    privacyPolicyUrl,
    eula
  } = _ref;
  if (privacyPolicyUrl && eula) {
    return null;
  }
  if (privacyPolicyUrl && !eula) {
    return /*#__PURE__*/_jsxs("p", {
      children: [partnerName, " has not provided any terms of use explaining your rights to use the app or how", ' ', partnerName, " may restrict your use of the app. By installing this app, you acknowledge and agree that ", partnerName, " may restrict or prohibit your use of the app at any time."]
    });
  }
  if (!privacyPolicyUrl && eula) {
    return /*#__PURE__*/_jsxs("p", {
      children: [partnerName, " has not provided a privacy policy explaining how this app will use your data. By installing this app, you acknowledge and agree this app may have unrestricted use of your data. If you do not agree, you should not install this app."]
    });
  }
  return /*#__PURE__*/_jsxs("p", {
    children: ["This vendor has not provided terms of use or a privacy policy explaining how they will use your data. By installing this app, you acknowledge and agree this app may have unrestricted use of your data and/or ", partnerName, " may restrict or prohibit your use of the app at any time. If you do not agree, you should not install this app."]
  });
};
const TermsOfUse = _ref2 => {
  let {
    appName,
    links,
    partnerName,
    onExternalLinkClick,
    privacyPolicyUrl,
    eula,
    deploymentType
  } = _ref2;
  const marketplaceTermsOfUse = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("li", {
      children: ["permit Atlassian to share anonymized data with ", appName]
    }), /*#__PURE__*/_jsxs("li", {
      children: ["agree to Atlassian Marketplace's", ' ', /*#__PURE__*/_jsx(ExternalLink, {
        href: links.marketplaceTermsOfUse.expand({}),
        onClick: () => onExternalLinkClick(AnalyticsSubjectIds.marketplaceTermsOfUse),
        "data-testid": AnalyticsSubjectIds.marketplaceTermsOfUse,
        children: "terms of use"
      })]
    })]
  });
  const partnerTermsOfUse = /*#__PURE__*/_jsxs("li", {
    children: ["agree to ", partnerName, "'s", ' ', eula && /*#__PURE__*/_jsx(ExternalLink, {
      href: eula,
      onClick: () => {
        onExternalLinkClick(AnalyticsSubjectIds.termsOfUse);
      },
      "data-testid": AnalyticsSubjectIds.termsOfUse,
      children: "terms of use"
    }), eula && privacyPolicyUrl && /*#__PURE__*/_jsx(_Fragment, {
      children: " and "
    }), privacyPolicyUrl && /*#__PURE__*/_jsx(ExternalLink, {
      href: privacyPolicyUrl,
      onClick: () => onExternalLinkClick(AnalyticsSubjectIds.privacyPolicy),
      "data-testid": AnalyticsSubjectIds.privacyPolicy,
      children: "privacy policy"
    })]
  });
  const forgeAppLogAccess = /*#__PURE__*/_jsxs("li", {
    children: ["agree to enabling ", partnerName, "'s access to log data for this app. You can", ' ', /*#__PURE__*/_jsx(ExternalLink, {
      href: "https://support.atlassian.com/security-and-access-policies/docs/manage-your-users-third-party-apps",
      onClick: () => onExternalLinkClick(AnalyticsSubjectIds.managelogAccess),
      children: "disable this access, or download logs"
    }), ' ', "at any time, from", ' ', /*#__PURE__*/_jsx(ExternalLink, {
      href: links.adminHub.expand({}),
      children: "admin.atlassian.com"
    })]
  });
  return /*#__PURE__*/_jsxs("span", {
    children: ["By installing this app, you:", /*#__PURE__*/_jsxs("ul", {
      children: [marketplaceTermsOfUse, (eula || privacyPolicyUrl) && partnerTermsOfUse, deploymentType === 'MarketplaceCloudAppDeployment' && forgeAppLogAccess]
    })]
  });
};
export const InstallableAppPrivacyInformation = _ref3 => {
  let {
    links,
    appName,
    partner,
    deploymentType,
    privacyPolicyUrl,
    endUserLicenseAgreementUrl,
    appIdentifier
  } = _ref3;
  const fireAnalyticsEvent = useAnalyticsEvents();
  const {
    name: partnerName,
    type: partnerType
  } = partner || {};
  const onExternalLinkClick = actionSubjectId => {
    fireAnalyticsEvent({
      actionSubjectId,
      eventType: ButtonClickedEvent,
      objectType: 'app',
      objectId: appIdentifier
    });
  };
  if (partnerType === MarketplacePartnerType.AtlassianInternal) {
    return null;
  }
  if (!partnerName) {
    return /*#__PURE__*/_jsx("div", {
      children: "This third party vendor has not provided a privacy policy or terms of use. Atlassian's Privacy Policy is not applicable to the use of this app."
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(TermsOfUse, {
      eula: endUserLicenseAgreementUrl,
      privacyPolicyUrl: privacyPolicyUrl,
      deploymentType: deploymentType,
      partnerName: partnerName,
      appName: appName,
      links: links,
      onExternalLinkClick: onExternalLinkClick
    }), /*#__PURE__*/_jsx(MissingPolicyWarning, {
      partnerName: partnerName,
      privacyPolicyUrl: privacyPolicyUrl,
      eula: endUserLicenseAgreementUrl
    })]
  });
};